import React, { useEffect, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import BotonAcceso from '../elementos/BotonAcceso.jsx';
import { useTheme } from "../guardarToken/themeContext";
import {
    ClipboardCheck,
    Calendar,
    Home,
    Clock,
    Edit3,
    AlertTriangle,
    Info,
    CheckCircle2,
    MapPin,
    MoveVertical,
    UtensilsCrossed,
    ArrowRight
} from 'lucide-react';

const apiUrlVistaPedidosActual = process.env.REACT_APP_API_VISTAPEDIDOS_ACTUAL;
const apiUrlVistaPedidosProxima = process.env.REACT_APP_API_VISTAPEDIDOS_PROXIMA;
const apiUrlCanEdit = process.env.REACT_APP_API_PEDIDOS + "can-edit";
const apiUrlPedidos = process.env.REACT_APP_API_PEDIDOS;

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return isMobile;
}

function parseFechaLocal(fecha) {
    if (!fecha) return null;
    if (fecha instanceof Date) return new Date(fecha);

    const fechaStr = String(fecha).slice(0, 10);
    const [year, month, day] = fechaStr.split("-").map(Number);
    if (!year || !month || !day) {
        const parsed = new Date(fecha);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return new Date(year, month - 1, day);
}

function getWeekRange(dateStr, offset = 0) {
    const d = parseFechaLocal(dateStr);
    if (!d) return [new Date(), new Date()];
    const day = (d.getDay() + 6) % 7;
    const monday = new Date(d);
    monday.setDate(d.getDate() - day + offset * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return [monday, sunday];
}

function fmt(dt) {
    return dt.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
}

function VistaPedidosComparativo() {
    const { isDarkMode } = useTheme();
    const [vistapedidosActual, setVistaPedidosActual] = useState([]);
    const [vistapedidosProxima, setVistaPedidosProxima] = useState([]);
    const [sedes, setSedes] = useState({});
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState(null);

    const usuario = localStorage.getItem("nombreUsuario") || "anon";
    const ORDER_KEY = `vistaPedidosOrder_${usuario}`;
    const defaultOrder = ["actual", "proxima"];
    const [order, setOrder] = useState(() => {
        const saved = localStorage.getItem(ORDER_KEY);
        return saved ? JSON.parse(saved) : defaultOrder;
    });

    const [canEdit, setCanEdit] = useState(false);
    const [checking, setChecking] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        getVistaPedidosActual();
        getVistaPedidosProxima();
        fetchSedes();
    }, []);

    const fetchSedes = async () => {
        try {
            const authToken = localStorage.getItem("token");
            const response = await axios.get('/api/sedes', {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            const map = {};
            response.data.forEach(s => map[s.id] = s.nombre);
            setSedes(map);
        } catch (error) {
            console.error("Error cargando sedes", error);
        }
    };

    const getVistaPedidosActual = async () => {
        try {
            const usuario = localStorage.getItem("nombreUsuario");
            if (!usuario) return;
            const response = await axios.get(`${apiUrlVistaPedidosActual}${usuario}`);
            setVistaPedidosActual(response.data);
        } catch (error) {
            console.error("Error al obtener los pedidos de la semana en curso.", error);
        }
    };

    const getVistaPedidosProxima = async () => {
        try {
            const usuario = localStorage.getItem("nombreUsuario");
            if (!usuario) return;
            const response = await axios.get(`${apiUrlVistaPedidosProxima}${usuario}`);
            setVistaPedidosProxima(response.data);
            setErr(null);
        } catch (error) {
            console.error("Error al obtener los pedidos de la semana próxima.", error);
            setErr('No se pudo cargar la semana próxima');
        }
    };

    const semanaProximaRango = useMemo(() => {
        const hoy = new Date();
        const [monday, sunday] = getWeekRange(hoy, 1);
        return `${fmt(monday)} al ${fmt(sunday)}`;
    }, []);

    useEffect(() => {
        const checkEdit = async () => {
            const hoy = new Date();
            const [monday] = getWeekRange(hoy, 1);
            const yyyy = monday.getFullYear();
            const mm = String(monday.getMonth() + 1).padStart(2, "0");
            const dd = String(monday.getDate()).padStart(2, "0");
            const fechaReferencia = `${yyyy}-${mm}-${dd}`;
            try {
                setChecking(true);
                const { data } = await axios.get(apiUrlCanEdit, { params: { fecha: fechaReferencia } });
                setCanEdit(data.canEdit);
            } catch {
                setCanEdit(false);
            } finally {
                setChecking(false);
            }
        };
        checkEdit();
    }, []);

    const handleEditar = () => {
        const hoy = new Date();
        const [monday] = getWeekRange(hoy, 1);
        const yyyy = monday.getFullYear();
        const mm = String(monday.getMonth() + 1).padStart(2, "0");
        const dd = String(monday.getDate()).padStart(2, "0");
        const semanaBase = `${yyyy}-${mm}-${dd}`;
        navigate("/user/seleccion", { state: { editMode: true, semanaBase } });
    };

    const handleEditarDia = (fecha) => {
        navigate("/user/seleccion", { state: { editMode: true, semanaBase: null, editarDia: fecha } });
    };

    const handleToggleHomeOffice = async (fecha, currentValue) => {
        const usuarioNombre = localStorage.getItem("nombreUsuario");
        const newValue = !currentValue;

        try {
            setMsg(null);
            setErr(null);
            setVistaPedidosProxima(prev =>
                prev.map(p => p.fecha === fecha ? { ...p, es_home_office: newValue } : p)
            );
            await axios.patch(`${apiUrlPedidos}home-office`, {
                usuario: usuarioNombre,
                fecha,
                es_home_office: newValue
            });
            if (newValue) {
                setMsg(`🏠 Marcado como Home Office`);
            } else {
                setMsg(`✏️ Día desmarcado - Usa el botón "Editar" para seleccionar un menú`);
            }
            getVistaPedidosProxima();
        } catch (error) {
            console.error('Error al actualizar home office:', error);
            setErr(error.response?.data?.message || 'No se pudo actualizar');
            setVistaPedidosProxima(prev =>
                prev.map(p => p.fecha === fecha ? { ...p, es_home_office: currentValue } : p)
            );
        }
    };

    const bloques = {
        actual: (
            <div className={`flex-1 rounded-3xl border transition-all duration-500 overflow-hidden ${isDarkMode ? "bg-slate-900 border-slate-800 shadow-2xl" : "bg-white border-slate-200 shadow-xl shadow-slate-200/50"
                }`}>
                <div className={`p-6 border-b flex items-center justify-between ${isDarkMode ? "bg-black/10 border-slate-800" : "bg-slate-50 border-slate-100"}`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
                            <Clock className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-black uppercase tracking-widest text-indigo-400">Semana Actual</h3>
                            <p className={`text-[10px] font-bold ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Consumo en sede</p>
                        </div>
                    </div>
                </div>

                <div className="p-4 overflow-x-auto overflow-y-auto min-h-[300px] max-h-[60vh]">
                    {vistapedidosActual.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 grayscale opacity-40">
                            <UtensilsCrossed className="w-12 h-12 mb-3 text-slate-400" />
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Sin pedidos registrados</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-separate border-spacing-y-2">
                            <thead>
                                <tr className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    <th className="px-4 py-2">Día / Fecha</th>
                                    <th className="px-4 py-2">Menú</th>
                                    <th className="px-4 py-2">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vistapedidosActual.map(order => {
                                    const fechaObj = parseFechaLocal(order.fecha);
                                    const dia = fechaObj.toLocaleDateString('es-AR', { weekday: 'short' });
                                    return (
                                        <tr key={order.id_pedido} className={`group transition-all ${isDarkMode ? "hover:bg-slate-800/40" : "hover:bg-slate-50"}`}>
                                            <td className="px-4 py-3 rounded-l-2xl border-y border-l border-transparent group-hover:border-slate-800">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-black uppercase tracking-tight text-indigo-500">{dia}</span>
                                                    <span className={`text-[10px] font-bold ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{order.fecha}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 border-y border-transparent group-hover:border-slate-800">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-black tracking-tight">{order.nombre}</span>
                                                    <span className={`text-[9px] font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-600" : "text-slate-400"}`}>{order.tipo}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 rounded-r-2xl border-y border-r border-transparent group-hover:border-slate-800">
                                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-500 w-fit">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                    <span className="text-[9px] font-black uppercase tracking-widest">Enviado</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        ),
        proxima: (
            <div className={`flex-1 rounded-3xl border transition-all duration-500 overflow-hidden ${isDarkMode ? "bg-slate-900 border-slate-800 shadow-2xl" : "bg-white border-slate-200 shadow-xl shadow-slate-200/50"
                }`}>
                <div className={`p-6 border-b flex items-center justify-between ${isDarkMode ? "bg-black/10 border-slate-800" : "bg-slate-50 border-slate-100"}`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-fuchsia-500/10 flex items-center justify-center border border-fuchsia-500/20">
                            <Calendar className="w-5 h-5 text-fuchsia-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-black uppercase tracking-widest text-fuchsia-400">Próxima Semana</h3>
                            <p className={`text-[10px] font-bold ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{semanaProximaRango}</p>
                        </div>
                    </div>
                    {canEdit && vistapedidosProxima.length > 0 && (
                        <button
                            onClick={handleEditar}
                            className="p-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-all shadow-lg"
                        >
                            <Edit3 className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="p-4 overflow-x-auto overflow-y-auto min-h-[300px] max-h-[60vh]">
                    {msg && <div className="mb-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest animate-in zoom-in duration-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}
                    {err && <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest animate-in zoom-in duration-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {err}</div>}

                    {vistapedidosProxima.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 grayscale-0">
                            <Calendar className="w-12 h-12 mb-3 text-fuchsia-500/50" />
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">Sin pedidos para la semana próxima</p>
                            <BotonAcceso
                                variant="accent"
                                onClick={() => navigate("/user/seleccion")}
                                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black text-[10px] uppercase tracking-widest px-6 py-2.5 rounded-xl shadow-lg shadow-fuchsia-950/20 flex items-center gap-2 transition-all"
                            >
                                Seleccionar Menú <ArrowRight className="w-3.5 h-3.5" />
                            </BotonAcceso>
                        </div>
                    ) : (
                        <table className="w-full text-left border-separate border-spacing-y-2">
                            <thead>
                                <tr className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    <th className="px-4 py-2">Día / Fecha</th>
                                    <th className="px-4 py-2">Menú / Sede</th>
                                    <th className="px-4 py-2 text-center">Home Office</th>
                                    {canEdit && <th className="px-4 py-2 text-center">Editar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {vistapedidosProxima.map((order) => {
                                    const fechaObj = parseFechaLocal(order.fecha);
                                    const dia = fechaObj.toLocaleDateString('es-AR', { weekday: 'short' });
                                    const isHO = order.es_home_office || false;

                                    return (
                                        <tr key={order.id_pedido} className={`group transition-all ${isHO ? "opacity-60" : ""} ${isDarkMode ? "hover:bg-slate-800/40" : "hover:bg-slate-50"}`}>
                                            <td className="px-4 py-3 rounded-l-2xl border-y border-l border-transparent group-hover:border-slate-800">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-black uppercase tracking-tight text-fuchsia-500">{dia}</span>
                                                    <span className={`text-[10px] font-bold ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{order.fecha}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 border-y border-transparent group-hover:border-slate-800">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                        {order.nombre ? (
                                                            <span className={`text-[11px] font-black tracking-tight ${isHO ? "line-through text-slate-600" : ""}`}>{order.nombre}</span>
                                                        ) : (
                                                            !isHO && <span className="text-[11px] font-black tracking-tight text-red-500 italic flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Sin menú seleccionado</span>
                                                        )}
                                                        {isHO && <span className="text-[8px] font-black uppercase px-1.5 bg-amber-500/20 text-amber-500 rounded border border-amber-500/30">HO</span>}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <MapPin className="w-2.5 h-2.5 text-slate-600" />
                                                        <span className={`text-[9px] font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-600" : "text-slate-400"}`}>
                                                            {isHO ? "--" : (sedes[order.sede_id] || "No especificada")}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 ${canEdit ? '' : 'rounded-r-2xl'} border-y ${canEdit ? '' : 'border-r'} border-transparent group-hover:border-slate-800 text-center align-middle`}>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={isHO}
                                                        onChange={() => handleToggleHomeOffice(order.fecha, isHO)}
                                                    />
                                                    <div className="w-9 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500 shadow-inner"></div>
                                                </label>
                                            </td>
                                            {canEdit && (
                                                <td className="px-4 py-3 rounded-r-2xl border-y border-r border-transparent group-hover:border-slate-800 text-center align-middle">
                                                    <button
                                                        onClick={() => handleEditarDia(order.fecha)}
                                                        className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-fuchsia-400 hover:border-fuchsia-500/50 transition-all shadow-lg hover:shadow-fuchsia-500/20"
                                                        title="Editar este día"
                                                    >
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {vistapedidosProxima.length > 0 && (
                    <div className={`p-4 mx-4 mb-4 rounded-2xl flex items-start gap-3 ${isDarkMode ? "bg-slate-950/50 border border-slate-800" : "bg-slate-50 border border-slate-100"}`}>
                        <Info className="w-4 h-4 text-fuchsia-500 mt-0.5" />
                        <p className={`text-[10px] font-bold leading-relaxed ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            <strong className="text-slate-300">HOME OFFICE:</strong> Si marcas un día como HO, tu pedido no se preparará para esa sede y no aparecerá en el reporte de cocina.
                        </p>
                    </div>
                )}
            </div>
        ),
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const newOrder = Array.from(order);
        const [moved] = newOrder.splice(result.source.index, 1);
        newOrder.splice(result.destination.index, 0, moved);
        setOrder(newOrder);
        localStorage.setItem(ORDER_KEY, JSON.stringify(newOrder));
    };

    const isMobile = useIsMobile();
    const droppableDirection = isMobile ? "vertical" : "horizontal";

    return (
        <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>
            <div className="max-w-7xl mx-auto">

                {/* HEADER PREMIUM */}
                <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-8 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}>
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-indigo-500 to-fuchsia-600 p-3 rounded-xl shadow-lg shadow-indigo-900/20">
                            <ClipboardCheck className="text-white w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-xl font-black tracking-tight leading-none mb-1">Mi Agenda de Menú</h1>
                            <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Visualiza y gestiona tus próximas viandas</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 mt-4 md:mt-0">
                        <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${isDarkMode ? "bg-slate-950 border-slate-800 text-slate-500" : "bg-slate-50 border-slate-100 text-slate-400"}`}>
                            <MoveVertical className="w-3.5 h-3.5" /> Reordenar paneles
                        </div>
                        {!canEdit && vistapedidosProxima.length > 0 && (
                            <div className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                <AlertTriangle className="w-3.5 h-3.5" /> Ventana Cerrada
                            </div>
                        )}
                    </div>
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="bloques" direction={droppableDirection}>
                        {(provided) => (
                            <div
                                className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-8`}
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {order.map((bloqueId, idx) => (
                                    <Draggable key={bloqueId} draggableId={bloqueId} index={idx}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`transition-all duration-300 w-full flex-1 max-w-[700px] ${snapshot.isDragging ? "z-50 scale-105 rotate-2" : ""}`}
                                                style={{ ...provided.draggableProps.style }}
                                            >
                                                {bloques[bloqueId]}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {/* FOOTER INFO */}
                <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-8 text-center animate-in slide-in-from-bottom duration-1000">
                    <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDarkMode ? "bg-slate-900 border border-slate-800" : "bg-white border border-slate-200"}`}>
                            <Edit3 className="w-5 h-5 text-indigo-400" />
                        </div>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-1">Flexibilidad</h4>
                        <p className={`text-[10px] font-bold max-w-[150px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Edita tus pedidos antes de la fecha límite</p>
                    </div>
                    <div className="hidden md:block w-12 h-px bg-slate-800" />
                    <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDarkMode ? "bg-slate-900 border border-slate-800" : "bg-white border border-slate-200"}`}>
                            <Home className="w-5 h-5 text-amber-500" />
                        </div>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-1">Home Office</h4>
                        <p className={`text-[10px] font-bold max-w-[150px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Gestiona tus ausencias para evitar desperdicios</p>
                    </div>
                    <div className="hidden md:block w-12 h-px bg-slate-800" />
                    <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDarkMode ? "bg-slate-900 border border-slate-800" : "bg-white border border-slate-200"}`}>
                            <MapPin className="w-5 h-5 text-emerald-500" />
                        </div>
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-1">Ubicación</h4>
                        <p className={`text-[10px] font-bold max-w-[150px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Retira en la sede de tu preferencia por día</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VistaPedidosComparativo;
